import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntil, filter } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import {
  Prescription,
  Fulfillment,
  FulfillDrug,
  DrawerType,
  DrawerParms
} from '@care-portals/api-interfaces';
import { DrawerStateFacade } from '../../../+store/drawer-state/drawer-state.facade';
import { PrescriptionFacade } from '../../../+store/prescription/prescription.facade';
import { OrdersFacade } from '../../../+store/order/order.facade';

@Component({
  selector: 'my-health-admin-create-fulfillment',
  templateUrl: './fulfillment.component.html',
  styleUrls: ['./fulfillment.component.less']
})
export class FulfillmentComponent implements OnInit, OnDestroy {
  @Input() rx: Prescription;
  validateForm!: FormGroup;
  tableData: FulfillDrug[] = [];
  quantities: { [key: string]: { authQty: number; remainQty: number } } = {};
  isDestroyed = new Subject();
  orderId: number;
  showModal: boolean = false;
  validationError: string = '';
  updating$ = this.prescriptionFacade.updating$;

  constructor(
    private fb: FormBuilder,
    private nzDrawerRef: NzDrawerRef,
    private drawerFacade: DrawerStateFacade,
    private prescriptionFacade: PrescriptionFacade,
    private orderFacade: OrdersFacade
  ) {
    this.drawerFacade.popped$
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(res => {
        if (
          res?.name === DrawerType.ORDER_CREATE ||
          res?.name === DrawerType.FIND_ORDER
        ) {
          const result = res.props;
          if (result) {
            this.validateForm.setValue({ orderId: result });
          }
        }
      });

    this.prescriptionFacade.fulfillmentOrderId$
      .pipe(
        takeUntil(this.isDestroyed),
        filter(orderId => !!orderId)
      )
      .subscribe(orderId => {
        this.orderFacade.loadOrder(orderId);
        this.showModal = true;
      });
  }

  ngOnInit() {
    this.validateForm = this.fb.group({
      orderId: [null, [Validators.required]]
    });

    this.orderFacade.selectedId$
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(orderId => {
        if (!this.validateForm.get('orderId').value) {
          this.orderId = orderId ? Number(orderId) : null;
          this.validateForm.setValue({ orderId: this.orderId });
        }
      });

    this.updateData();
  }

  ngOnDestroy() {
    this.prescriptionFacade.resetFulfillmentState();
    this.validateForm.reset();
    this.tableData = [];
    this.quantities = {};
    this.isDestroyed.next(true);
    this.isDestroyed.complete();
  }

  updateData() {
    this.tableData = this.rx.drugs.map(drug => {
      this.quantities[drug.din] = {
        authQty: drug.mitte * (drug.refills + 1),
        remainQty: drug.mitte * (drug.refills + 1)
      };
      return {
        din: drug.din,
        qty: 0,
        name: drug.drugName,
        mitte: drug.mitte,
        refills: drug.refills,
        txNumber: ''
      };
    });

    if (this.rx.fulfillments) {
      this.rx.fulfillments.map((fulfill: Fulfillment) => {
        fulfill.drugs?.forEach(drug => {
          if(this.quantities[drug.din]) {
            this.quantities[drug.din].remainQty -= drug.qty;
          }
        });
      });
    }
  }

  validQty(din: string = '', setError: boolean = false): boolean {
    let qty = 0;

    for (const drug of this.tableData) {
      if (din && drug.din !== din) {
        continue;
      }

      if (drug.qty !== 0 && drug.qty > this.quantities[drug.din].remainQty) {
        if (setError) {
          this.validationError = `Dispensing Qty for DIN #${drug.din} is over remaining quantity left.`;
        }
        return false;
      }
      qty += drug.qty;
    }

    if (!din && !qty) {
      if (setError) {
        this.validationError = 'No drug dispensing quantity specified.';
      }
      return false;
    }

    return true;
  }

  openCreateOrder() {
    this.drawerFacade.openDrawer({
      name: DrawerType.ORDER_CREATE,
      props: { customerId: this.customerId }
    } as DrawerParms);
  }

  openFindOrder() {
    this.drawerFacade.openDrawer({
      name: DrawerType.FIND_ORDER,
      props: { customerId: this.customerId }
    } as DrawerParms);
  }

  get customerId() {
    return this.rx.customer['_id'] || this.rx.customer;
  }

  addFulfillment() {
    this.validationError = '';

    Object.keys(this.validateForm.controls).map(i => {
      this.validateForm.controls[i].markAsDirty();
      this.validateForm.controls[i].updateValueAndValidity();
    });

    if (!this.validateForm.valid) {
      if (!this.validateForm.get('orderId').value) {
        this.validationError = 'Please select or create an order before adding fulfillment.';
      } else {
        this.validationError = 'Please ensure all required fields are filled correctly.';
      }
      return;
    }

    if (!this.validQty('', true)) {
      return;
    }

    const fulfillment = {} as Fulfillment;
    fulfillment.drugs = this.tableData.filter(drug => drug.qty > 0);
    fulfillment.orderId = this.validateForm.get('orderId').value;
    fulfillment.status = 'new';
    fulfillment.notes = '';
    fulfillment.createdAt = new Date();

    this.prescriptionFacade.addFulfillment(this.rx._id, fulfillment);
  }

  handleNo() {
    this.showModal = false;
    this.prescriptionFacade.resetFulfillmentState();
    this.nzDrawerRef.close(true);
  }

  handleYes() {
    this.orderFacade.updateOrder(this.validateForm.get('orderId').value, {
      status: 'awaiting_shipment'
    });
    this.showModal = false;
    this.prescriptionFacade.resetFulfillmentState();
    this.nzDrawerRef.close(true);
  }
}
